import React from "react"
import { Grid, TextField, MenuItem, InputAdornment, Typography } from "@material-ui/core"
import { Info as InfoIcon, Warning as WarningIcon } from "@material-ui/icons"

import { SvgIcon, Divider } from "@/components"
import { AIIcon } from "@/assets/icons"

import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"

import {
	ChatBotFlowBlockContent,
	BoardCardFieldMode,
	BoardCardFieldContextCapacity
} from "@/protocols/chatBot"

import { themeColors } from "letalkui"

import useMoneyInputCents from "@/hooks/useMoneyInputCents"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/BoardCardFieldBlock/BoardCardFieldSectionEditor/styles"

const CONTEXT_CAPACITY_OPTIONS: Array<{ value: BoardCardFieldContextCapacity, label: string }> = [
	{ value: "low", label: "Baixo" },
	{ value: "medium", label: "Médio" },
	{ value: "high", label: "Alto" }
]

const CONTEXT_CAPACITY_TO_DESCRIPTION: Record<BoardCardFieldContextCapacity, string> = {
	low: "Lê as últimas 10 mensagens trocadas na conversa. É o menor consumo de créditos de IA.",
	medium: "Lê as últimas 50 mensagens trocadas na conversa. Consumo de créditos de IA moderado.",
	high: "Lê todas as mensagens trocadas na conversa. Traz a maior precisão, mas também o maior consumo de créditos de IA."
}

type BoardCardFieldSectionEditorProps = {
	sectionId: string
	onSave: (chatBotFlowBlockContent: Partial<ChatBotFlowBlockContent>) => void
	onModeChange: (mode: BoardCardFieldMode) => void
	chatBotFlowBlockContent: ChatBotFlowBlockContent
}

const BoardCardFieldSectionEditor: React.FC<BoardCardFieldSectionEditorProps> = (props) => {
	const {
		sectionId,
		chatBotFlowBlockContent,
		onSave,
		onModeChange,
		children
	} = props

	const classes = useStyles()

	const contentEditor = useContentEditor({ chatBotFlowBlockContent })

	const { cents, displayValue, handleKeyDown, handlePaste, setCents } = useMoneyInputCents(chatBotFlowBlockContent?.boardCardFieldData?.valueInCents)

	const currentMode: BoardCardFieldMode = chatBotFlowBlockContent?.boardCardFieldData?.mode || "manual"
	const currentContextCapacity: BoardCardFieldContextCapacity = contentEditor.content?.boardCardFieldData?.contextCapacity || "high"

	const handleModeChange = (mode: BoardCardFieldMode) => {
		onModeChange(mode)
	}

	const handleDrawerOpen = () => {
		contentEditor.reset()
		setCents(chatBotFlowBlockContent?.boardCardFieldData?.valueInCents ?? 0)
	}

	const handleContextCapacityChange = (capacity: BoardCardFieldContextCapacity) => {
		contentEditor.changeContent({
			boardCardFieldData: {
				contextCapacity: capacity
			}
		})
	}

	const handleSave = () => {
		onSave({
			boardCardFieldData: {
				...chatBotFlowBlockContent?.boardCardFieldData,
				fieldType: "contact-value",
				mode: currentMode,
				valueInCents: cents,
				contextCapacity: currentContextCapacity
			}
		})
	}

	const renderManualModeContent = () => {
		return (
			<Grid
				item
				xs={12}
			>
				<Typography className={classes.sectionLabel}>
					Valor a atribuir
				</Typography>

				<TextField
					fullWidth
					variant="outlined"
					placeholder="0,00"
					type="text"
					inputMode="decimal"
					value={displayValue}
					InputProps={{
						readOnly: true,
						startAdornment: (
							<InputAdornment position="start">
								R$
							</InputAdornment>
						)
					}}
					onKeyDown={handleKeyDown}
					onPaste={handlePaste}
				/>
			</Grid>
		)
	}

	const renderIAModeContent = () => {
		return (
			<>
				<Grid
					item
					xs={12}
				>
					<Grid
						container
						className={classes.infoBox}
					>
						<Grid
							item
							className={classes.infoBoxIcon}
						>
							<SvgIcon
								icon={AIIcon}
								fontSize="small"
								autoSize
								style={{ color: themeColors.colors.base.white }}
							/>
						</Grid>

						<Typography className={classes.infoBoxText}>
							A IA lê as mensagens da conversa e atribui automaticamente o valor final negociado. Se nenhum valor for encontrado, o fluxo segue por uma saída própria do bloco, que você pode direcionar para o passo que quiser.
						</Typography>
					</Grid>
				</Grid>

				<Grid
					item
					xs={12}
				>
					<Typography className={classes.sectionLabel}>
						Capacidade de contexto
					</Typography>

					<Typography className={classes.capacityDescription}>
						Define quantas mensagens a IA vai ler para extrair o valor. Quanto maior a capacidade, mais mensagens são analisadas e maior o consumo de créditos de IA.
					</Typography>

					<Grid
						container
						className={classes.segmentedControl}
					>
						{CONTEXT_CAPACITY_OPTIONS.map(option => {
							const isActive = currentContextCapacity === option.value

							return (
								<Grid
									key={option.value}
									item
									className={`${classes.segmentedPill} ${isActive ? classes.segmentedPillActive : classes.segmentedPillInactive}`}
									onClick={() => handleContextCapacityChange(option.value)}
								>
									{option.label}
								</Grid>
							)
						})}
					</Grid>

					<Grid
						container
						alignItems="flex-start"
						className={classes.capacityLevelDescription}
					>
						<InfoIcon
							fontSize="small"
							style={{ color: themeColors.colors.system.purple[600] }}
						/>

						<Divider orientation="vertical" size={1} />

						<Typography className={classes.capacityLevelText}>
							{CONTEXT_CAPACITY_TO_DESCRIPTION[currentContextCapacity]}
						</Typography>
					</Grid>
				</Grid>
			</>
		)
	}

	const modeToRenderFn: Record<BoardCardFieldMode, () => React.ReactNode> = {
		manual: renderManualModeContent,
		ia: renderIAModeContent
	}

	return (
		<BlockBodySectionEditor
			sectionId={sectionId}
			title="VALOR DO CONTATO NO FUNIL"
			onSave={handleSave}
			onOpen={handleDrawerOpen}
			AsideDrawerProps={{
				children: (
					<Grid
						container
						spacing={2}
					>
						<Grid
							item
							xs={12}
						>
							<Typography className={classes.sectionLabel}>
								Como definir o valor
							</Typography>

							<TextField
								fullWidth
								select
								variant="outlined"
								value={currentMode}
								onChange={event => handleModeChange(event.target.value as BoardCardFieldMode)}
							>
								<MenuItem value="manual">
									Preencher manualmente
								</MenuItem>

								<MenuItem value="ia">
									Capturar da conversa com IA
								</MenuItem>
							</TextField>
						</Grid>

						{modeToRenderFn[currentMode]()}

						<Grid
							item
							xs={12}
						>
							<Grid
								container
								alignItems="flex-start"
								className={classes.prerequisiteWarning}
							>
								<WarningIcon
									fontSize="small"
									style={{ color: themeColors.colors.warning[600] }}
								/>

								<Divider orientation="vertical" size={1} />

								<Typography className={classes.prerequisiteWarningText}>
									Este bloco só consegue gravar o valor se o contato já estiver no funil. Utilize o bloco &quot;Gerenciar contato no funil&quot; antes deste (ele tem a opção de adicionar o contato ao funil), ou garanta que o contato já foi inserido manualmente.
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				)
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default BoardCardFieldSectionEditor
